<template>
  <div class="classrooms-page">
    <div class="bg-deco" aria-hidden="true">
      <div class="bg-deco-icon i1"><img src="../assets/images/stars.png" alt="" /></div>
      <div class="bg-deco-icon i2"><img src="../assets/images/cloud3.png" alt="" /></div>
      <div class="bg-deco-icon i3"><img src="../assets/images/rainbow2.png" alt="" /></div>
      <div class="bg-deco-icon i4"><img src="../assets/images/butterfly2.png" alt="" /></div>
      <div class="bg-deco-icon i5"><img src="../assets/images/award.png" alt="" /></div>
    </div>

    <b-loading :is-full-page="true" :active.sync="isLoading"></b-loading>
    <b-modal
      :active.sync="modalCreateClassroom"
      has-modal-card
      trap-focus
      :destroy-on-hide="true"
      aria-role="dialog"
      aria-modal
    >
      <set-classroom @over="modalCreateClassroom = false" />
    </b-modal>

    <div class="page-header">
      <div>
        <h1 class="page-title">Classrooms</h1>
        <p class="page-subtitle" v-if="getUserSchoolName">{{ getUserSchoolName }}</p>
      </div>
      <button v-if="canManage" class="btn-primary" @click="modalCreateClassroom = true">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
        <span>Create Classroom</span>
      </button>
    </div>

    <div class="filters-panel">
      <div class="filter-field">
        <label class="filter-label">Subject</label>
        <div class="select-wrap">
          <select v-model="filter.subject" @change="filterBy()">
            <option :value="null">All subjects</option>
            <option v-for="subject in fetchSubjects" :key="subject._id" :value="subject._id">{{ subject.name }}</option>
          </select>
        </div>
      </div>
      <div class="filter-field">
        <label class="filter-label">Grade</label>
        <div class="select-wrap">
          <select v-model="filter.grade" @change="filterBy()">
            <option :value="null">All grades</option>
            <option v-for="grade in fetchGrades" :key="grade._id" :value="grade._id">{{ grade.name }}</option>
          </select>
        </div>
      </div>
      <div class="filter-field">
        <label class="filter-label">Section</label>
        <div class="select-wrap">
          <select v-model="filter.section" @change="filterBy()">
            <option :value="null">All sections</option>
            <option v-for="section in fetchSections" :key="section._id" :value="section._id">{{ section.name }}</option>
          </select>
        </div>
      </div>
      <button v-if="hasActiveFilters" class="filter-clear" @click="clearFilters">Clear filters</button>
    </div>

    <div class="panel">
      <div class="panel-header">
        <div>
          <h2 class="panel-title">{{ canManage ? 'All Classrooms' : 'My Classrooms' }}</h2>
          <p class="panel-sub">{{ canManage ? 'Manage classrooms and assign teachers / students' : 'Classrooms you are assigned to teach' }}</p>
        </div>
      </div>
      <div class="panel-body">
        <ClassroomTable></ClassroomTable>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import ClassroomTable from '@/components/School/ClassroomTable.vue'
import SetClassroom from '@/components/School/SetClassroom.vue'

export default {
  name: 'Classrooms',
  components: { ClassroomTable, SetClassroom },
  data() {
    return {
      modalCreateClassroom: false,
      isLoading: false,
      filter: {
        subject: null,
        grade: null,
        section: null
      }
    }
  },
  computed: {
    ...mapGetters(['fetchGrades', 'fetchSections', 'fetchSubjects', 'getTeacherToken', 'getUserSchoolName', 'getUserType']),
    hasActiveFilters() {
      return !!(this.filter.subject || this.filter.grade || this.filter.section)
    },
    canManage() {
      return this.getUserType === 'SA' || this.getUserType === 'A'
    }
  },
  methods: {
    ...mapActions(['loadGrades', 'loadSections', 'loadSubjects', 'loadClassrooms']),
    filterBy() {
      this.isLoading = true
      this.loadClassrooms({ token: this.getTeacherToken, filter: this.filter })
        .finally(() => { this.isLoading = false })
    },
    clearFilters() {
      this.filter = { subject: null, grade: null, section: null }
      this.filterBy()
    }
  },
  async created() {
    this.loadGrades({ token: this.getTeacherToken })
    this.loadSubjects({ token: this.getTeacherToken })
    this.loadSections({ token: this.getTeacherToken })
  }
}
</script>

<style scoped>
.classrooms-page {
  position: relative;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: clamp(1rem, 2.5vw, 2rem) clamp(0.75rem, 2vw, 1.5rem);
  min-height: 100vh;
  background:
    radial-gradient(1100px 480px at 50% -120px, rgba(137, 25, 231, 0.08), transparent 60%),
    radial-gradient(900px 420px at 100% 100%, rgba(49, 200, 255, 0.10), transparent 60%);
}

.classrooms-page::after {
  content: '';
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  height: 38vh;
  max-height: 380px;
  background-image: url(../assets/images/cloud2.png);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: bottom center;
  pointer-events: none;
  z-index: 0;
  opacity: 0.85;
}

.bg-deco {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
}
.bg-deco-icon {
  position: absolute;
  width: clamp(36px, 5vw, 64px);
  opacity: 0.55;
  animation: bg-float 6s ease-in-out infinite;
}
.bg-deco-icon img { width: 100%; height: auto; display: block; }
.bg-deco-icon.i1 { top: 6%;  left: 4%;  animation-delay: 0s; }
.bg-deco-icon.i2 { top: 10%; right: 6%; animation-delay: -1.5s; }
.bg-deco-icon.i3 { top: 38%; right: 2%; animation-delay: -2.2s; width: clamp(50px, 7vw, 90px); }
.bg-deco-icon.i4 { top: 60%; left: 2%;  animation-delay: -3s; }
.bg-deco-icon.i5 { top: 80%; right: 7%; animation-delay: -4s; }

@keyframes bg-float {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50%      { transform: translateY(-12px) rotate(3deg); }
}

.page-header,
.filters-panel,
.panel {
  position: relative;
  z-index: 2;
}

.page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.page-title {
  font-size: clamp(1.5rem, 2.6vw, 2.2rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  margin: 0 0 0.15rem;
  background: linear-gradient(135deg, #8919e7 0%, #4361ee 60%, #31c8ff 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  line-height: 1.1;
}

.page-subtitle {
  font-size: 0.95rem;
  color: #64748b;
  margin: 0;
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, #8919e7, #6b38c7);
  color: #ffffff;
  border: none;
  padding: 0.65rem 1.1rem;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(137, 25, 231, 0.28);
  transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;
}
.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(137, 25, 231, 0.36);
}

.filters-panel {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr)) auto;
  gap: 0.75rem;
  align-items: end;
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 14px;
  padding: 1rem 1.1rem;
  margin-bottom: 1.1rem;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.05);
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  min-width: 0;
}
.filter-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #475569;
  letter-spacing: 0.01em;
}
.select-wrap {
  position: relative;
}
.select-wrap::after {
  content: '';
  position: absolute;
  right: 0.85rem;
  top: 50%;
  width: 8px;
  height: 8px;
  border-right: 2px solid #94a3b8;
  border-bottom: 2px solid #94a3b8;
  transform: translateY(-65%) rotate(45deg);
  pointer-events: none;
}
.select-wrap select {
  width: 100%;
  border: 1.5px solid #e5e7eb;
  background: #ffffff;
  border-radius: 10px;
  padding: 0.65rem 2rem 0.65rem 0.85rem;
  font-size: 0.92rem;
  color: #1f2937;
  outline: none;
  appearance: none;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.select-wrap select:hover {
  border-color: #cbd5e1;
}
.select-wrap select:focus {
  border-color: #8919e7;
  box-shadow: 0 0 0 3px rgba(137, 25, 231, 0.12);
}

.filter-clear {
  align-self: end;
  background: transparent;
  color: #64748b;
  border: 1px solid rgba(15, 23, 42, 0.1);
  font-size: 0.85rem;
  font-weight: 600;
  padding: 0.62rem 0.9rem;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.filter-clear:hover {
  background: rgba(15, 23, 42, 0.04);
  color: #1f2937;
}

.panel {
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 16px;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.05);
  overflow: hidden;
}
.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 1.1rem 1.25rem;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  background: linear-gradient(180deg, #fafbfd, #ffffff);
}
.panel-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}
.panel-sub {
  font-size: 0.85rem;
  color: #64748b;
  margin: 0.15rem 0 0;
}
.panel-body {
  padding: 1rem 1.25rem 1.25rem;
}

@media screen and (max-width: 900px) {
  .filters-panel {
    grid-template-columns: 1fr 1fr;
  }
  .filter-clear {
    grid-column: 1 / -1;
    justify-self: stretch;
  }
}

@media screen and (max-width: 640px) {
  .filters-panel {
    grid-template-columns: 1fr;
  }
  .page-header {
    align-items: flex-start;
  }
  .btn-primary {
    width: 100%;
    justify-content: center;
  }
  .bg-deco-icon.i2,
  .bg-deco-icon.i4 { display: none; }
}
</style>
